import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { 
  Package, 
  Plus, 
  Trash2, 
  Save, 
  Truck, 
  Building2, 
  Calendar,
  Hash,
  AlertCircle,
  CheckCircle,
  Loader2,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { formatQty3, isPartialQtyInput, parseQtyInput, resolveQtyFromInput } from '../utils/quantityUtils';
import { itemCategoryApiService, ItemCategory } from '../services/itemCategoryApi';
import { rawItemApiService, RawItem } from '../services/rawItemApi';
import { supplierApiService, Supplier } from '../services/supplierApi';
import { purchaseOrderApiService, PoHeader } from '../services/purchaseOrderApi';
import { grnApiService } from '../services/grnApi';
import { useAuth } from '../contexts/AuthContext';

interface ReceivedItem {
  id: string;
  categoryId: number | null;
  itemId: number | null;
  quantity: number;
  unit: string;
  heatNumber: string;
  batchNumber: string;
  expiryDate: string;
  remarks: string;
}

export function ManualGRNEntry() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [receiveDate, setReceiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [dcNo, setDcNo] = useState('');
  const [dcDate, setDcDate] = useState(new Date().toISOString().split('T')[0]);
  const [defaultSupplier, setDefaultSupplier] = useState<Supplier | null>(null);
  const [defaultPO, setDefaultPO] = useState<PoHeader | null>(null);
  const [receivedItems, setReceivedItems] = useState<ReceivedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // API data
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [rawItems, setRawItems] = useState<RawItem[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PoHeader[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [qtyInputs, setQtyInputs] = useState<Record<string, string>>({});

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingData(true);
        
        // Load categories, raw items, suppliers, and purchase orders
        const [categoriesData, rawItemsData, suppliersResponse, purchaseOrdersData] = await Promise.all([
          itemCategoryApiService.getItemCategories(),
          rawItemApiService.getRawItems(),
          supplierApiService.getSuppliers(),
          user?.defaultBranchId 
            ? purchaseOrderApiService.getPONumbersByBranch(user.defaultBranchId)
            : Promise.resolve([])
        ]);

        setCategories(categoriesData);
        setRawItems(rawItemsData);
        
        // Extract suppliers from API response
        const suppliersData = suppliersResponse.success ? suppliersResponse.data : [];
        setSuppliers(suppliersData);
        
        setPurchaseOrders(purchaseOrdersData);

        // Set default supplier (ID 1)
        const defaultSupplierById = suppliersData.find(s => s.id === 1);
        if (defaultSupplierById) {
          setDefaultSupplier(defaultSupplierById);
        }

        // Set default PO (ID 1)
        const defaultPOById = purchaseOrdersData.find(po => po.id === 1);
        if (defaultPOById) {
          setDefaultPO(defaultPOById);
        }

      } catch (error) {
        // console.error('Error loading data:', error);
        toast.error('Failed to load initial data');
      } finally {
        setLoadingData(false);
      }
    };

    if (user?.defaultBranchId) {
      loadData();
    }
  }, [user?.defaultBranchId]);

  const addReceivedItem = () => {
    const newItem: ReceivedItem = {
      id: Date.now().toString(),
      categoryId: null,
      itemId: null,
      quantity: 0,
      unit: '',
      heatNumber: '',
      batchNumber: '',
      expiryDate: '',
      remarks: ''
    };
    setReceivedItems([...receivedItems, newItem]);
  };

  const updateReceivedItem = (id: string, field: keyof ReceivedItem, value: any) => {
    setReceivedItems(prev => prev.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        
        // If category changes, clear the item and unit
        if (field === 'categoryId') {
          updatedItem.itemId = null;
          updatedItem.unit = '';
        }
        
        // If item changes, auto-populate the unit
        if (field === 'itemId') {
          const selectedItem = rawItems.find(rawItem => rawItem.id === value);
          if (selectedItem) {
            updatedItem.unit = selectedItem.itemUnit;
          }
        }

        if (field === 'quantity') {
          updatedItem.quantity =
            typeof value === 'number' ? value : parseQtyInput(String(value));
        }
        
        return updatedItem;
      }
      return item;
    }));
  };

  const removeReceivedItem = (id: string) => {
    setReceivedItems(prev => prev.filter(item => item.id !== id));
  };

  const getItemsByCategory = (categoryId: number) => {
    return rawItems.filter(item => item.itemCategory?.id === categoryId);
  };

  const handleSave = async () => {
    if (!dcNo || dcNo.trim() === '') {
      toast.error('DC Number is required. Please enter DC Number before creating GRN.');
      return;
    }

    if (receivedItems.length === 0) {
      toast.error('Please add at least one received item');
      return;
    }

    // Validate received items (resolve pending qty inputs before save)
    const itemsToSave = receivedItems.map((item) => ({
      ...item,
      quantity: resolveQtyFromInput(qtyInputs[item.id], item.quantity),
    }));

    const invalidItems = itemsToSave.filter(
      (item) => !item.categoryId || !item.itemId || item.quantity <= 0,
    );

    if (invalidItems.length > 0) {
      toast.error('Please fill in all required fields for received items');
      return;
    }

    setSaving(true);
    try {
      // Use default supplier and PO if available, otherwise use ID 1
      const selectedSupplier = defaultSupplier || suppliers.find(s => s.id === 1);
      const selectedPO = defaultPO || purchaseOrders.find(po => po.id === 1);

      if (!selectedSupplier || !selectedPO) {
        toast.error('No default supplier or PO available. Please ensure suppliers and POs exist.');
        return;
      }

      // Log the default values being used
      // console.log('Creating GRN with default values:');
      // console.log('Default Supplier:', selectedSupplier.supplierName, `(${selectedSupplier.supplierCode})`);
      // console.log('Default PO:', selectedPO.poNumber);

      // Create GRN header
      const grnHeaderData = {
        grnDate: receiveDate,
        receiveDate: receiveDate,
        poHeaderId: selectedPO.id,
        supplierId: selectedSupplier.id,
        status: 'Received',
        receivedBy: 'System', // TODO: Get from user context
        vehicleNumber: 'N/A',
        driverName: 'N/A',
        totalAmount: 0, // Will be calculated from items
        notes: 'Manual raw material receipt entry',
        type: 'MANUAL' as const,
        isActive: true,
        branchId: 1, // TODO: Get from context
        storeId: 1, // TODO: Get from context or user selection
        dcNo: dcNo.trim(),
        dcDate: dcDate,
        createdBy: 1, // TODO: Get from context
        updatedBy: 1
      };

      const createdGRN = await grnApiService.createGRN(grnHeaderData);

      // Create GRN details for each received item
      const grnDetailPromises = itemsToSave.map(async (item) => {
        const selectedItem = rawItems.find(rawItem => rawItem.id === item.itemId);
        if (!selectedItem) {
          throw new Error(`Raw item not found for ID: ${item.itemId}`);
        }

        const quantity = item.quantity;
        const grnDetailData = {
          lineNumber: itemsToSave.indexOf(item) + 1, // Sequential line number
          quantity,
          receivedQuantity: quantity,
          unitPrice: 0, // No cost required for manual entry
          lineTotal: 0, // No cost calcreated from Manulaculation needed
          qualityCheck: 'Pending',
          remarks: item.remarks,
          tcNo: 'N/A',
          tcDate: new Date().toISOString().split('T')[0],
          supplierHeatNo: item.batchNumber || 'N/A',
          nop: undefined,
          branchId: 1, // TODO: Get from context
          storeId: createdGRN.storeId, // use store from header (set earlier)
          grnId: createdGRN.id,
          rawItemId: item.itemId!,
          createdBy: 1, // TODO: Get from context
          updatedBy: 1
        };

        return await grnApiService.createGRNDetail(grnDetailData);
      });

      await Promise.all(grnDetailPromises);

      toast.success('Manual GRN created successfully!');
      
      // Navigate to GRN listing page
      navigate('/grn');

    } catch (error) {
      // console.error('Error creating GRN:', error);
      toast.error('Failed to create GRN');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setReceivedItems([]);
    setReceiveDate(new Date().toISOString().split('T')[0]);
    setDcNo('');
    setDcDate(new Date().toISOString().split('T')[0]);
    toast.info('Form reset');
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
          Manual GRN Entry
        </h1>
        <p className="text-gray-600">Enter multiple received goods with default PO and supplier association</p>
      </div>

      {/* Receive Date and DC Information */}
      <div className="flex justify-center">
        <div className="w-full max-w-2xl grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="receiveDate" className="text-lg font-medium text-gray-700">Receive Date</Label>
            <Input
              id="receiveDate"
              type="date"
              value={receiveDate}
              onChange={(e) => setReceiveDate(e.target.value)}
              className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 mt-2"
            />
          </div>
          <div>
            <Label htmlFor="dcNo" className="text-lg font-medium text-gray-700">
              DC Number <span className="text-red-500">*</span>
            </Label>
            <Input
              id="dcNo"
              type="text"
              value={dcNo}
              onChange={(e) => setDcNo(e.target.value)}
              placeholder="Enter DC Number"
              className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 mt-2"
              required
            />
          </div>
          <div>
            <Label htmlFor="dcDate" className="text-lg font-medium text-gray-700">DC Date</Label>
            <Input
              id="dcDate"
              type="date"
              value={dcDate}
              onChange={(e) => setDcDate(e.target.value)}
              className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 mt-2"
            />
          </div>
        </div>
      </div>

      {/* Default Values Info */}
      <div className="flex justify-center">
        <div className="w-full max-w-md bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600 space-y-2">
            <div className="flex items-center gap-2">
              <span className="font-medium">Default Supplier:</span>
              <span className="text-gray-800">
                {defaultSupplier ? `${defaultSupplier.supplierName} (${defaultSupplier.supplierCode})` : 'Not available'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium">Default PO:</span>
              <span className="text-gray-800">
                {defaultPO ? `${defaultPO.poNumber}` : 'Not available'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Received Items Table */}
      <Card className="border-primary-200 shadow-lg shadow-primary-100/50">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
          <CardTitle className="flex items-center gap-2 text-primary-800">
            <FileText className="h-5 w-5 text-primary-700" />
            Received Items
          </CardTitle>
          <CardDescription>
            Add multiple received goods with their details (no HSN or cost required)
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-600">
              Select category and item for each received good (manual entry)
            </p>
            <Button
              onClick={addReceivedItem}
              className="bg-primary-500 text-gray-700 hover:bg-gray-50"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </div>

          {receivedItems.length > 0 ? (
            <div className="border border-primary-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                  <TableRow className="border-primary-200">
                    <TableHead className="text-left">Category</TableHead>
                    <TableHead className="text-left">Item</TableHead>
                    <TableHead className="text-center">Quantity</TableHead>
                    <TableHead className="text-center">Unit</TableHead>
                    <TableHead className="text-center">Batch No.</TableHead>
                    <TableHead className="text-center">Expiry Date</TableHead>
                    <TableHead className="text-left">Remarks</TableHead>
                    <TableHead className="text-center w-20">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {receivedItems.map((item) => (
                    <TableRow key={item.id} className="border-primary-100">
                      <TableCell>
                        <Select 
                          value={item.categoryId?.toString() || ''} 
                          onValueChange={(value) => updateReceivedItem(item.id, 'categoryId', parseInt(value))}
                        >
                          <SelectTrigger className="border-primary-200">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((category) => (
                              <SelectItem key={category.id} value={category.id.toString()}>
                                {category.categoryName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={item.itemId?.toString() || ''} 
                          onValueChange={(value) => updateReceivedItem(item.id, 'itemId', parseInt(value))}
                          disabled={!item.categoryId}
                        >
                          <SelectTrigger className="border-primary-200">
                            <SelectValue placeholder={item.categoryId ? "Select item" : "Select category first"} />
                          </SelectTrigger>
                          <SelectContent>
                            {item.categoryId && getItemsByCategory(item.categoryId).map((rawItem) => (
                              <SelectItem key={rawItem.id} value={rawItem.id.toString()}>
                                {rawItem.itemName} ({rawItem.sku})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="text"
                          value={
                            qtyInputs[item.id] ??
                            (item.quantity ? formatQty3(item.quantity) : '')
                          }
                          onChange={(e) => {
                            const value = e.target.value;
                            if (!isPartialQtyInput(value)) return;
                            setQtyInputs(prev => ({ ...prev, [item.id]: value }));
                            if (value && !value.endsWith('.')) {
                              updateReceivedItem(item.id, 'quantity', parseQtyInput(value));
                            } else if (value === '' || value === '.') {
                              updateReceivedItem(item.id, 'quantity', 0);
                            }
                          }}
                          onBlur={(e) => {
                            updateReceivedItem(item.id, 'quantity', parseQtyInput(e.target.value.trim() || '0'));
                            setQtyInputs(prev => {
                              const { [item.id]: _, ...rest } = prev;
                              return rest;
                            });
                          }}
                          className="border-primary-200 text-center"
                          placeholder="0.000"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={item.unit}
                          readOnly
                          className="border-primary-200 bg-gray-50 text-center"
                          placeholder="Auto"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={item.batchNumber}
                          onChange={(e) => updateReceivedItem(item.id, 'batchNumber', e.target.value)}
                          className="border-primary-200 text-center"
                          placeholder="Batch No."
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="date"
                          value={item.expiryDate}
                          onChange={(e) => updateReceivedItem(item.id, 'expiryDate', e.target.value)}
                          className="border-primary-200 text-center"
                        />
                      </TableCell>
                      <TableCell>
                        <Textarea
                          value={item.remarks}
                          onChange={(e) => updateReceivedItem(item.id, 'remarks', e.target.value)}
                          className="border-primary-200 resize-none"
                          placeholder="Remarks"
                          rows={2}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => removeReceivedItem(item.id)}
                          className="text-red-600 hover:bg-red-50 border-red-200"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-lg font-medium mb-2">No Received Goods</p>
              <p className="text-sm">Click "Add Item" to start adding received goods for manual GRN entry.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-end gap-4">
        <Button 
          variant="outline" 
          onClick={handleCancel}
          className="border-primary-200 text-primary-700 hover:bg-primary-50 hover:border-primary-300"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          disabled={saving || receivedItems.length === 0}
          className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg shadow-primary-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {saving ? 'Creating GRN...' : 'Save GRN'}
        </Button>
      </div>
    </div>
  );
}
